import React, {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  getSafeAiBillingError,
  platformAccessApi,
} from "../api/platformAccessApi";
import {
  publishTenantAiWalletSummary,
  requestTenantAiWalletRefresh,
} from "../aiWalletRefresh";
import "./TenantAiWalletTopUpDialog.css";

const POLL_INTERVAL_MS = 4000;
const IN_PROGRESS_STATUSES = new Set(["pending", "verification_required"]);
const PAYMENT_STATUSES = new Set([
  "pending",
  "completed",
  "failed",
  "cancelled",
  "verification_required",
]);

function positiveSafeInteger(value) {
  const number = Number(value);
  return Number.isSafeInteger(number) && number > 0;
}

function formatCredit(microUsd) {
  if (!positiveSafeInteger(microUsd)) return "Amount unavailable";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(microUsd || 0) / 1000000);
}

function formatCharge(amountMinor, currency) {
  const currencyCode = String(currency || "").toUpperCase();
  if (!positiveSafeInteger(amountMinor) || !/^[A-Z]{3}$/.test(currencyCode)) {
    return "Amount unavailable";
  }
  return new Intl.NumberFormat("en-ZA", {
    style: "currency",
    currency: currencyCode,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(amountMinor || 0) / 100);
}

function validConfiguration(configuration) {
  return Boolean(
    typeof configuration?.enabled === "boolean" &&
    positiveSafeInteger(configuration?.minChargeAmountMinor) &&
    positiveSafeInteger(configuration?.maxChargeAmountMinor) &&
    configuration.maxChargeAmountMinor >= configuration.minChargeAmountMinor &&
    configuration?.chargeCurrency === "ZAR" &&
    configuration?.creditCurrency === "USD" &&
    String(configuration?.pricingDisclosure || "").trim(),
  );
}

function amountZarToMinor(value) {
  const match = /^(\d+)(?:\.(\d{1,2}))?$/.exec(String(value || "").trim());
  if (!match) return null;
  const major = Number(match[1]);
  const cents = Number(String(match[2] || "").padEnd(2, "0") || 0);
  const amountMinor = major * 100 + cents;
  return Number.isSafeInteger(amountMinor) ? amountMinor : null;
}

function normalizeAmountZar(value) {
  const amountMinor = amountZarToMinor(value);
  if (amountMinor === null) return "";
  return `${Math.floor(amountMinor / 100)}.${String(amountMinor % 100).padStart(2, "0")}`;
}

// Same shape as a ZAR amount, but for the USD amount the tenant actually
// wants credited. There is no client-side USD->ZAR conversion here: the
// server is the only source of truth for the official rate plus margin (see
// probeThenRefineQuote below), this just validates the raw typed number.
function amountUsdToMicros(value) {
  const match = /^(\d+)(?:\.(\d{1,2}))?$/.exec(String(value || "").trim());
  if (!match) return null;
  const major = Number(match[1]);
  const cents = Number(String(match[2] || "").padEnd(2, "0") || 0);
  const micros = (major * 100 + cents) * 10000;
  return Number.isSafeInteger(micros) ? micros : null;
}

function validFxSnapshot(snapshot) {
  const rate = Number(snapshot?.zarPerUsd);
  return Boolean(
    Number.isFinite(rate) && rate > 0 &&
    /^\d+(?:\.\d+)?$/.test(String(snapshot?.zarPerUsd || "")) &&
    String(snapshot?.sourceName || "").trim() &&
    /^\d{4}-\d{2}-\d{2}$/.test(String(snapshot?.observedOn || "")) &&
    String(snapshot?.policyVersion || "").trim() &&
    String(snapshot?.roundingPolicy || "").trim(),
  );
}

function validQuote(quote) {
  return Boolean(
    String(quote?.quoteId || "").trim() &&
    positiveSafeInteger(quote?.creditMicroUsd) &&
    positiveSafeInteger(quote?.chargeAmountMinor) &&
    quote?.creditCurrency === "USD" &&
    quote?.chargeCurrency === "ZAR" &&
    quote?.expiresAt &&
    validFxSnapshot(quote?.fxSnapshot) &&
    String(quote?.pricingDisclosure || "").trim(),
  );
}

function validPayment(payment) {
  return Boolean(
    String(payment?.paymentId || "").trim() &&
    PAYMENT_STATUSES.has(payment?.status) &&
    positiveSafeInteger(payment?.creditMicroUsd) &&
    positiveSafeInteger(payment?.chargeAmountMinor) &&
    payment?.chargeCurrency === "ZAR" &&
    payment?.creditCurrency === "USD",
  );
}

function formatExpiry(value) {
  const date = new Date(value);
  if (!value || Number.isNaN(date.getTime())) return "the time shown by Callpay";
  return new Intl.DateTimeFormat("en-ZA", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function formatObservedOn(value) {
  const date = new Date(`${value}T00:00:00.000Z`);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(value || "")) ||
      Number.isNaN(date.getTime())) return "Date unavailable";
  return new Intl.DateTimeFormat("en-ZA", {
    dateStyle: "medium",
    timeZone: "UTC",
  }).format(date);
}

function formatFxRate(snapshot) {
  return validFxSnapshot(snapshot) ?
    `1 USD = R${String(snapshot.zarPerUsd)}` : "Rate unavailable";
}

function secondsUntil(value, now = Date.now()) {
  const expiresAt = Date.parse(value);
  if (!Number.isFinite(expiresAt)) return null;
  return Math.max(0, Math.ceil((expiresAt - now) / 1000));
}

function formatCountdown(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) return "Time unavailable";
  if (seconds === 0) return "Expired";
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${String(remainingSeconds).padStart(2, "0")}`;
}

function displayStatus(status) {
  const value = String(status || "pending").replace(/[_-]+/g, " ");
  return `${value.charAt(0).toUpperCase()}${value.slice(1)}`;
}

function hostedPaymentUrl(value) {
  try {
    const parsed = new URL(String(value || ""));
    const hostname = parsed.hostname.toLowerCase();
    const callpayHost = hostname === "callpay.com" ||
      hostname.endsWith(".callpay.com");
    return parsed.protocol === "https:" && callpayHost ?
      parsed.toString() : "";
  } catch (_error) {
    return "";
  }
}

function createIdempotencyKey() {
  const cryptoApi = typeof window !== "undefined" ? window.crypto : null;
  if (typeof cryptoApi?.randomUUID === "function") {
    return `wallet-top-up-${cryptoApi.randomUUID()}`;
  }
  if (typeof cryptoApi?.getRandomValues === "function") {
    const bytes = new Uint8Array(16);
    cryptoApi.getRandomValues(bytes);
    return `wallet-top-up-${Array.from(bytes, (value) =>
      value.toString(16).padStart(2, "0")).join("")}`;
  }
  return `wallet-top-up-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function focusableElements(container) {
  if (!container) return [];
  return Array.from(container.querySelectorAll(
    "a[href], button:not([disabled]), input:not([disabled]), " +
    "select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])",
  )).filter((element) => !element.hidden && element.getAttribute("aria-hidden") !== "true");
}

function statusMessage(status) {
  switch (status) {
    case "completed":
      return "Callpay payment confirmed. Your AI Wallet has been updated by the server.";
    case "failed":
      return "Callpay could not complete this payment. No wallet credit was applied.";
    case "cancelled":
      return "This payment was cancelled. No wallet credit was applied.";
    case "verification_required":
      return "Callpay has returned the payment for additional verification. Your wallet will only update after the server confirms it.";
    default:
      return "Payment is awaiting server confirmation. Keep this window open or check again after returning from Callpay.";
  }
}

export default function TenantAiWalletTopUpDialog({
  open,
  onClose,
  onCompleted,
}) {
  const headingId = useId();
  const descriptionId = useId();
  const amountHelpId = useId();
  const amountInputId = useId();
  const dialogRef = useRef(null);
  const amountInputRef = useRef(null);
  const stageFocusRef = useRef(null);
  const closeRef = useRef(onClose);
  const completionRef = useRef(onCompleted);
  const sessionRef = useRef(0);
  const statusRequestRef = useRef(null);
  const mutationRequestRef = useRef(null);
  const quoteRequestRef = useRef(null);
  const completedPaymentsRef = useRef(new Set());
  const idempotencyKeyRef = useRef("");
  const [fundingConfiguration, setFundingConfiguration] = useState(null);
  const [amountUsd, setAmountUsd] = useState("");
  const [quote, setQuote] = useState(null);
  const [payment, setPayment] = useState(null);
  const [quoteSecondsRemaining, setQuoteSecondsRemaining] = useState(null);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [handedOff, setHandedOff] = useState(false);
  const [configurationLoadAttempt, setConfigurationLoadAttempt] = useState(0);
  const [outstandingPayment, setOutstandingPayment] = useState(null);
  const [connectionTest, setConnectionTest] = useState({
    busy: false,
    error: "",
    url: "",
    merchantReference: "",
  });

  closeRef.current = onClose;
  completionRef.current = onCompleted;

  const reset = useCallback(() => {
    setFundingConfiguration(null);
    setAmountUsd("");
    setQuote(null);
    setPayment(null);
    setQuoteSecondsRemaining(null);
    setBusy("");
    setError("");
    setHandedOff(false);
    setOutstandingPayment(null);
    setConnectionTest({ busy: false, error: "", url: "", merchantReference: "" });
    idempotencyKeyRef.current = "";
    statusRequestRef.current = null;
    mutationRequestRef.current = null;
    quoteRequestRef.current = null;
  }, []);

  // Isolated Callpay connectivity check. Unlike the quote/payment flow above,
  // this calls the standalone makePaymentCallPay callable directly: it
  // creates a real R1.00 Callpay charge to verify the shared merchant
  // profile end to end, but it never touches the AI Wallet ledger — no
  // wallet credit is applied no matter how the resulting payment resolves.
  const runConnectionTest = useCallback(async () => {
    setConnectionTest({ busy: true, error: "", url: "", merchantReference: "" });
    try {
      const callable = httpsCallable(getFunctions(), "makePaymentCallPay");
      const result = await callable({ amount: "1.00" });
      const url = hostedPaymentUrl(result?.data?.hostedUrl);
      if (!url) throw new Error("Callpay did not return a valid payment link.");
      setConnectionTest({
        busy: false,
        error: "",
        url,
        merchantReference: String(result?.data?.merchantReference || ""),
      });
    } catch (testError) {
      setConnectionTest({
        busy: false,
        error: getSafeAiBillingError(
          testError,
          "The Callpay connection test failed.",
        ),
        url: "",
        merchantReference: "",
      });
    }
  }, []);

  const close = useCallback(() => {
    sessionRef.current += 1;
    closeRef.current?.();
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    sessionRef.current += 1;
    reset();
    const opener = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const focusTimer = window.setTimeout(() => dialogRef.current?.focus(), 0);
    return () => {
      window.clearTimeout(focusTimer);
      sessionRef.current += 1;
      statusRequestRef.current = null;
      document.body.style.overflow = previousOverflow;
      if (typeof opener?.focus === "function") opener.focus();
    };
  }, [open, reset]);

  useEffect(() => {
    if (!open) return undefined;
    const session = sessionRef.current;
    let cancelled = false;
    const loadConfiguration = async () => {
      setBusy("configuration");
      setError("");
      try {
        const result = await platformAccessApi
          .listMyTenantAiWalletTopUpOffers();
        if (cancelled || session !== sessionRef.current) return;
        const outstanding = result?.outstandingPayment;
        // An outstanding payment is shown as a dismissable notice on the
        // amount form, never forced as the whole screen: the tenant admin
        // must always be free to start a new payment (the server already
        // allows a genuinely new quote to supersede an old outstanding one).
        setOutstandingPayment(
          validPayment(outstanding) &&
            IN_PROGRESS_STATUSES.has(outstanding.status) ? outstanding : null,
        );
        if (!validConfiguration(result)) {
          throw new Error("The AI Wallet funding configuration is incomplete.");
        }
        setFundingConfiguration(result);
        if (result.enabled) {
          window.setTimeout(() => amountInputRef.current?.focus(), 0);
        }
      } catch (nextError) {
        if (!cancelled && session === sessionRef.current) {
          setError(getSafeAiBillingError(
            nextError,
            "AI Wallet funding details could not be loaded. Please close this window and try again.",
          ));
        }
      } finally {
        if (!cancelled && session === sessionRef.current) setBusy("");
      }
    };
    loadConfiguration();
    return () => {
      cancelled = true;
    };
  }, [configurationLoadAttempt, open]);

  useEffect(() => {
    if (!open || !quote?.expiresAt || payment) {
      setQuoteSecondsRemaining(null);
      return undefined;
    }
    const updateCountdown = () => {
      setQuoteSecondsRemaining(secondsUntil(quote.expiresAt));
    };
    updateCountdown();
    const timer = window.setInterval(updateCountdown, 1000);
    return () => window.clearInterval(timer);
  }, [open, payment, quote?.expiresAt]);

  const announceCompletion = useCallback((result) => {
    const paymentId = String(result?.paymentId || "");
    if (!paymentId || completedPaymentsRef.current.has(paymentId)) return;
    completedPaymentsRef.current.add(paymentId);
    if (!publishTenantAiWalletSummary(result?.wallet)) {
      requestTenantAiWalletRefresh();
    }
    completionRef.current?.(result);
  }, []);

  const applyPayment = useCallback((result) => {
    if (!validPayment(result)) {
      throw new Error("The payment service returned an incomplete status.");
    }
    setPayment((current) => ({ ...current, ...result }));
    if (result.status === "completed") announceCompletion(result);
    return result;
  }, [announceCompletion]);

  const checkStatus = useCallback(async (paymentId, { automatic = false } = {}) => {
    if (!paymentId || statusRequestRef.current) return null;
    const requestToken = {};
    statusRequestRef.current = requestToken;
    const session = sessionRef.current;
    if (!automatic) setBusy("status");
    setError("");
    try {
      const result = await platformAccessApi.getMyTenantAiWalletTopUp({
        paymentId,
      });
      if (session !== sessionRef.current) return null;
      if (String(result?.paymentId || "") !== String(paymentId)) {
        throw new Error("The payment service returned a mismatched status.");
      }
      return applyPayment(result);
    } catch (nextError) {
      if (session === sessionRef.current) {
        setError(getSafeAiBillingError(
          nextError,
          "We could not confirm this payment yet. Your wallet has not been updated; please check again.",
        ));
      }
      return null;
    } finally {
      if (statusRequestRef.current === requestToken) {
        statusRequestRef.current = null;
      }
      if (session === sessionRef.current && !automatic) setBusy("");
    }
  }, [applyPayment]);

  useEffect(() => {
    const paymentId = payment?.paymentId;
    if (!open || !handedOff || !paymentId ||
        !IN_PROGRESS_STATUSES.has(payment?.status)) return undefined;
    let cancelled = false;
    let timer = null;
    const poll = () => {
      timer = window.setTimeout(async () => {
        if (cancelled) return;
        const result = await checkStatus(paymentId, { automatic: true });
        if (!cancelled && (!result ||
            IN_PROGRESS_STATUSES.has(result?.status))) poll();
      }, POLL_INTERVAL_MS);
    };
    poll();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [checkStatus, handedOff, open, payment?.paymentId, payment?.status]);

  // Once the server confirms the wallet credit, show the tick briefly, then
  // close on its own rather than making the tenant admin click Done.
  useEffect(() => {
    if (!open || payment?.status !== "completed") return undefined;
    const timer = window.setTimeout(() => close(), 2200);
    return () => window.clearTimeout(timer);
  }, [close, open, payment?.status]);

  // The server only prices ZAR->USD (it owns the official rate + margin, and
  // no client-side conversion is trusted for money). To offer a USD-first
  // amount field anyway, request a small probe quote first to learn the
  // server's current effective rate, use that to estimate the ZAR needed for
  // the tenant's target USD credit, then request the real, binding quote for
  // that ZAR amount. The review screen always shows the server's actual
  // final ZAR charge and USD credit before payment, so an estimate here can
  // never mis-charge anyone.
  const probeThenRefineQuote = async (targetUsdMicros) => {
    const probeAmountZar = normalizeAmountZar(
      String(Math.max(fundingConfiguration.minChargeAmountMinor, 100) / 100),
    );
    const probe = await platformAccessApi.quoteMyTenantAiWalletTopUp({
      amountZar: probeAmountZar,
    });
    if (!validQuote(probe)) {
      throw new Error("The payment service returned an incomplete quote.");
    }
    const estimatedMinor = Math.ceil(
      (targetUsdMicros * probe.chargeAmountMinor) / probe.creditMicroUsd,
    );
    const clampedMinor = Math.min(
      Math.max(estimatedMinor, fundingConfiguration.minChargeAmountMinor),
      fundingConfiguration.maxChargeAmountMinor,
    );
    const finalAmountZar = normalizeAmountZar(String(clampedMinor / 100));
    return platformAccessApi.quoteMyTenantAiWalletTopUp({
      amountZar: finalAmountZar,
    });
  };

  // Requests (or refreshes) a binding quote for the given USD target and
  // applies the result. Shared by the typing-debounce effect below and the
  // automatic refresh that fires when a quote's countdown reaches zero, so
  // the tenant admin never has to click a separate "recalculate" control.
  // Uses "latest wins" semantics rather than blocking: if the amount changes
  // again before an in-flight request resolves, that newer request's token
  // wins and the stale response is discarded instead of stalling typing.
  const runQuote = useCallback(async (targetUsdMicros) => {
    if (!fundingConfiguration) return;
    const requestToken = {};
    quoteRequestRef.current = requestToken;
    const session = sessionRef.current;
    setBusy("quote");
    setError("");
    try {
      const result = await probeThenRefineQuote(targetUsdMicros);
      if (session !== sessionRef.current ||
          quoteRequestRef.current !== requestToken) return;
      if (!validQuote(result)) {
        throw new Error("The payment service returned an incomplete quote.");
      }
      setQuote(result);
      setQuoteSecondsRemaining(secondsUntil(result.expiresAt));
      idempotencyKeyRef.current = createIdempotencyKey();
    } catch (nextError) {
      if (session === sessionRef.current &&
          quoteRequestRef.current === requestToken) {
        setQuote(null);
        setError(getSafeAiBillingError(
          nextError,
          "A secure Callpay quote could not be prepared. Please try again.",
        ));
      }
    } finally {
      if (session === sessionRef.current &&
          quoteRequestRef.current === requestToken) setBusy("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fundingConfiguration]);

  // Auto-calculates the quote a short moment after the tenant admin stops
  // typing an amount, instead of requiring an explicit "review price" click.
  useEffect(() => {
    if (!open || payment || !fundingConfiguration) return undefined;
    const trimmed = amountUsd.trim();
    if (!trimmed) {
      quoteRequestRef.current = null;
      setQuote(null);
      setQuoteSecondsRemaining(null);
      return undefined;
    }
    const targetUsdMicros = amountUsdToMicros(trimmed);
    const timer = window.setTimeout(() => {
      if (!positiveSafeInteger(targetUsdMicros)) {
        quoteRequestRef.current = null;
        setQuote(null);
        setQuoteSecondsRemaining(null);
        setError("Enter a valid US dollar amount using no more than two decimal places.");
        return;
      }
      runQuote(targetUsdMicros);
    }, 700);
    return () => window.clearTimeout(timer);
  }, [amountUsd, fundingConfiguration, open, payment, runQuote]);

  // If the tenant admin leaves the dialog open long enough for the quote to
  // expire, refresh it automatically for the same amount rather than leaving
  // them stuck behind a dead "Quote expired" state.
  useEffect(() => {
    if (!open || payment || !quote || quoteSecondsRemaining !== 0) return undefined;
    const targetUsdMicros = amountUsdToMicros(amountUsd);
    if (!positiveSafeInteger(targetUsdMicros)) return undefined;
    runQuote(targetUsdMicros);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteSecondsRemaining]);

  const createPayment = async () => {
    if (mutationRequestRef.current || busy === "quote" || !quote?.quoteId ||
        secondsUntil(quote.expiresAt) === 0) return;
    const requestToken = {};
    mutationRequestRef.current = requestToken;
    const session = sessionRef.current;
    if (!idempotencyKeyRef.current) {
      idempotencyKeyRef.current = createIdempotencyKey();
    }
    setBusy("create");
    setError("");
    try {
      const result = await platformAccessApi.createMyTenantAiWalletTopUp({
        quoteId: quote.quoteId,
        idempotencyKey: idempotencyKeyRef.current,
      });
      if (session !== sessionRef.current) return;
      if (!validPayment(result)) {
        throw new Error("The payment service returned an incomplete payment.");
      }
      applyPayment(result);
      const hostedUrl = hostedPaymentUrl(result.hostedUrl);
      if (hostedUrl && IN_PROGRESS_STATUSES.has(result.status)) {
        // A real new tab, not the iframe modal: Callpay's own post-payment
        // redirect breaks when its checkout runs inside a frame (it lands on
        // a broken fallback page instead of its normal receipt page). A full
        // top-level tab is the one path already proven to work end to end.
        setHandedOff(true);
        window.open(hostedUrl, "_blank", "noopener,noreferrer");
      } else {
        setHandedOff(false);
        window.setTimeout(() => stageFocusRef.current?.focus(), 0);
      }
    } catch (nextError) {
      if (session === sessionRef.current) {
        setError(getSafeAiBillingError(
          nextError,
          "The Callpay payment could not be started. No wallet credit was applied.",
        ));
      }
    } finally {
      if (mutationRequestRef.current === requestToken) {
        mutationRequestRef.current = null;
      }
      if (session === sessionRef.current) setBusy("");
    }
  };

  const viewOutstandingPayment = () => {
    if (busy || !outstandingPayment) return;
    setQuote({
      quoteId: "resumed-payment",
      label: outstandingPayment.label,
      creditMicroUsd: outstandingPayment.creditMicroUsd,
      creditCurrency: outstandingPayment.creditCurrency,
      chargeAmountMinor: outstandingPayment.chargeAmountMinor,
      chargeCurrency: outstandingPayment.chargeCurrency,
      expiresAt: outstandingPayment.expiresAt,
      fxSnapshot: outstandingPayment.fxSnapshot,
      pricingDisclosure: outstandingPayment.pricingDisclosure,
    });
    setPayment(outstandingPayment);
    setHandedOff(true);
    window.setTimeout(() => stageFocusRef.current?.focus(), 0);
  };

  const changeAmount = () => {
    if (busy) return;
    sessionRef.current += 1;
    statusRequestRef.current = null;
    mutationRequestRef.current = null;
    quoteRequestRef.current = null;
    setQuote(null);
    setPayment(null);
    setHandedOff(false);
    setQuoteSecondsRemaining(null);
    setError("");
    idempotencyKeyRef.current = "";
    window.setTimeout(() => amountInputRef.current?.focus(), 0);
  };

  const restart = () => {
    if (busy) return;
    changeAmount();
    setFundingConfiguration(null);
    setAmountUsd("");
    setConfigurationLoadAttempt((value) => value + 1);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      close();
      return;
    }
    if (event.key !== "Tab") return;
    const focusable = focusableElements(dialogRef.current);
    if (!focusable.length) {
      event.preventDefault();
      dialogRef.current?.focus();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && (document.activeElement === first ||
        document.activeElement === dialogRef.current)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  if (!open || typeof document === "undefined") return null;

  const paymentUrl = hostedPaymentUrl(payment?.hostedUrl);
  const paymentStatus = String(payment?.status || "pending");
  const terminalFailure = paymentStatus === "failed" ||
    paymentStatus === "cancelled";
  const quoteExpired = !payment && quoteSecondsRemaining === 0;

  return createPortal(
    <div
      className="tenant-wallet-top-up__backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) close();
      }}
    >
      <section
        aria-describedby={descriptionId}
        aria-labelledby={headingId}
        aria-modal="true"
        className="tenant-wallet-top-up"
        onKeyDown={handleKeyDown}
        ref={dialogRef}
        role="dialog"
        tabIndex="-1"
      >
        <header className="tenant-wallet-top-up__heading">
          <div>
            <span>Secure wallet funding</span>
            <h2 id={headingId}>Add AI Wallet funds</h2>
          </div>
          <button aria-label="Close AI Wallet top-up" onClick={close} type="button">
            &times;
          </button>
        </header>

        <p className="tenant-wallet-top-up__intro" id={descriptionId}>
          Add prepaid USD funds for your organisation. Callpay processes the
          payment in South African rand; the server confirms the exact amounts
          before you continue.
        </p>

        {!payment ? (
          <form
            aria-label="Choose wallet funding amount"
            className="tenant-wallet-top-up__form"
            onSubmit={(event) => {
              event.preventDefault();
              if (quote && !quoteExpired) createPayment();
            }}
          >
            {busy === "configuration" ? (
              <p className="tenant-wallet-top-up__loading" role="status">
                Loading secure funding details...
              </p>
            ) : null}
            {outstandingPayment ? (
              <p className="tenant-wallet-top-up__notice">
                You have a payment awaiting confirmation ({formatCharge(
                  outstandingPayment.chargeAmountMinor,
                  outstandingPayment.chargeCurrency,
                )}). Starting a new one below does not cancel it. {" "}
                <button
                  className="tenant-wallet-top-up__inline-link"
                  onClick={viewOutstandingPayment}
                  type="button"
                >
                  View it
                </button>
              </p>
            ) : null}
            {fundingConfiguration ? (
              <label
                className="tenant-wallet-top-up__amount"
                htmlFor={amountInputId}
              >
                <span>Amount to add to your AI Wallet (USD)</span>
                <span className="tenant-wallet-top-up__amount-control">
                  <strong aria-hidden="true">$</strong>
                  <input
                    aria-label="Amount to add to your AI Wallet in US dollars"
                    aria-describedby={amountHelpId}
                    aria-invalid={Boolean(error)}
                    autoComplete="off"
                    inputMode="decimal"
                    id={amountInputId}
                    maxLength="14"
                    name="amountUsd"
                    onChange={(event) => {
                      setAmountUsd(event.target.value);
                      setError("");
                    }}
                    placeholder="0.00"
                    ref={amountInputRef}
                    spellCheck="false"
                    type="text"
                    value={amountUsd}
                  />
                </span>
              </label>
            ) : null}
            {fundingConfiguration ? (
              <p id={amountHelpId}>
                Callpay charges your organisation in South African rand, at
                the latest official SARB rate plus our 7.5% margin. Your
                price updates automatically as you type.
              </p>
            ) : null}
            {busy === "quote" ? (
              <p className="tenant-wallet-top-up__loading is-spinning" role="status">
                <span aria-hidden="true" className="tenant-wallet-top-up__spinner is-muted" />
                Calculating your Callpay price...
              </p>
            ) : null}
            {quote ? (
              <div
                className="tenant-wallet-top-up__review"
                ref={stageFocusRef}
                tabIndex="-1"
              >
                <h3>Review your top-up</h3>
                <dl className="tenant-wallet-top-up__meta">
                  <div>
                    <dt>You pay via Callpay</dt>
                    <dd>{formatCharge(quote.chargeAmountMinor, quote.chargeCurrency)}</dd>
                  </div>
                  <div>
                    <dt>Your AI Wallet receives</dt>
                    <dd>{formatCredit(quote.creditMicroUsd)}</dd>
                  </div>
                  <div>
                    <dt>Official exchange rate</dt>
                    <dd>{formatFxRate(quote.fxSnapshot)}</dd>
                  </div>
                  <div>
                    <dt>Rate source</dt>
                    <dd>
                      {quote.fxSnapshot.sourceName}
                      <small>Observed {formatObservedOn(quote.fxSnapshot.observedOn)}</small>
                    </dd>
                  </div>
                  <div>
                    <dt>Quote expires</dt>
                    <dd>
                      <span
                        aria-live="polite"
                        className={
                          Number.isFinite(quoteSecondsRemaining) &&
                            quoteSecondsRemaining <= 60 ?
                            "tenant-wallet-top-up__countdown is-urgent" :
                            "tenant-wallet-top-up__countdown"
                        }
                      >
                        {formatCountdown(quoteSecondsRemaining)}
                      </span>
                      <small>{formatExpiry(quote.expiresAt)}</small>
                    </dd>
                  </div>
                </dl>
                <p className="tenant-wallet-top-up__disclosure">
                  {quote.pricingDisclosure}
                </p>
                {quoteExpired ? (
                  <p className="tenant-wallet-top-up__warning" role="alert">
                    This quote expired. Refreshing your price automatically...
                  </p>
                ) : null}
              </div>
            ) : null}
            {fundingConfiguration && !fundingConfiguration.enabled ? (
              <>
                <p className="tenant-wallet-top-up__notice" role="status">
                  AI Wallet payments are not available for this organisation yet.
                  Submitting the amount above will be rejected by the server;
                  no payment will be created.
                </p>
                <p className="tenant-wallet-top-up__notice">
                  Diagnostic only: this button creates a real R1.00 Callpay
                  charge to verify the merchant connection. It never credits
                  the AI Wallet, regardless of the outcome.
                </p>
                {!connectionTest.url ? (
                  <button
                    aria-busy={connectionTest.busy}
                    className="tenant-wallet-top-up__primary"
                    disabled={connectionTest.busy}
                    onClick={runConnectionTest}
                    type="button"
                  >
                    {connectionTest.busy ?
                      "Creating test Callpay payment..." :
                      "Test Callpay connection (R1.00)"}
                  </button>
                ) : (
                  <a
                    className="tenant-wallet-top-up__payment-link"
                    href={connectionTest.url}
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    Open test Callpay payment
                    <span> (opens in a new tab)</span>
                    <span aria-hidden="true">&#8599;</span>
                  </a>
                )}
                {connectionTest.merchantReference ? (
                  <p className="tenant-wallet-top-up__notice">
                    Reference: {connectionTest.merchantReference}
                  </p>
                ) : null}
                {connectionTest.error ? (
                  <p className="tenant-wallet-top-up__warning" role="alert">
                    {connectionTest.error}
                  </p>
                ) : null}
              </>
            ) : null}
            {!fundingConfiguration && error ? (
              <button
                className="tenant-wallet-top-up__primary"
                disabled={Boolean(busy)}
                onClick={() => setConfigurationLoadAttempt((value) => value + 1)}
                type="button"
              >
                Retry funding details
              </button>
            ) : fundingConfiguration ? (
              <button
                aria-busy={Boolean(busy)}
                className="tenant-wallet-top-up__primary"
                disabled={Boolean(busy) || !quote || quoteExpired}
                type="submit"
              >
                {busy === "create" || busy === "quote" ? (
                  <span aria-hidden="true" className="tenant-wallet-top-up__spinner" />
                ) : null}
                {quoteExpired ? "Quote expired" :
                  busy === "create" ? "Starting payment..." :
                    busy === "quote" ? "Calculating price..." :
                      !quote ? "Enter an amount to continue" :
                        "Pay"}
              </button>
            ) : null}
          </form>
        ) : null}

        {payment ? (
          <div className={`tenant-wallet-top-up__payment is-${paymentStatus}`}>
            <div
              aria-live="polite"
              className="tenant-wallet-top-up__status"
              ref={stageFocusRef}
              role="status"
              tabIndex="-1"
            >
              <span
                aria-hidden="true"
                className={`tenant-wallet-top-up__status-icon is-${paymentStatus}`}
              >
                {paymentStatus === "completed" ? "\u2713" :
                  terminalFailure ? "\u2715" :
                    <span className="tenant-wallet-top-up__spinner" />}
              </span>
              <div>
                <small>{displayStatus(paymentStatus)}</small>
                <strong>{statusMessage(paymentStatus)}</strong>
              </div>
            </div>
            <dl>
              <div>
                <dt>Wallet funds</dt>
                <dd>{formatCredit(payment.creditMicroUsd)}</dd>
              </div>
              <div>
                <dt>Callpay charge</dt>
                <dd>{formatCharge(payment.chargeAmountMinor, payment.chargeCurrency)}</dd>
              </div>
              {validFxSnapshot(payment.fxSnapshot) ? (
                <div>
                  <dt>Official exchange rate</dt>
                  <dd>
                    {formatFxRate(payment.fxSnapshot)}
                    <small>
                      {payment.fxSnapshot.sourceName} &middot; observed {" "}
                      {formatObservedOn(payment.fxSnapshot.observedOn)}
                    </small>
                  </dd>
                </div>
              ) : null}
            </dl>

            {payment.pricingDisclosure ? (
              <p className="tenant-wallet-top-up__disclosure">
                {payment.pricingDisclosure}
              </p>
            ) : null}

            {paymentStatus !== "completed" && !terminalFailure && paymentUrl ? (
              <button
                className="tenant-wallet-top-up__payment-link"
                onClick={() => {
                  setHandedOff(true);
                  window.open(paymentUrl, "_blank", "noopener,noreferrer");
                }}
                type="button"
              >
                Continue to secure Callpay payment
                <span> (opens in a new tab)</span>
                <span aria-hidden="true">&#8599;</span>
              </button>
            ) : null}
            {paymentStatus !== "completed" && !terminalFailure && !paymentUrl ? (
              <p className="tenant-wallet-top-up__warning" role="alert">
                The secure payment link is unavailable. No wallet credit has
                been applied. Contact support if the payment remains
                unresolved.
              </p>
            ) : null}
            {handedOff && IN_PROGRESS_STATUSES.has(paymentStatus) &&
                payment?.nextCheckAt ? (
              <p className="tenant-wallet-top-up__notice">
                Automatic confirmation remains active. The next secure provider
                check is available after {formatExpiry(payment.nextCheckAt)}.
              </p>
            ) : null}

            <div className="tenant-wallet-top-up__actions">
              {paymentStatus === "completed" ? (
                <button className="tenant-wallet-top-up__primary" onClick={close} type="button">
                  Done
                </button>
              ) : terminalFailure ? (
                <button className="tenant-wallet-top-up__primary" onClick={restart} type="button">
                  Try another top-up
                </button>
              ) : (
                <>
                  <button disabled={Boolean(busy)} onClick={changeAmount} type="button">
                    Change amount
                  </button>
                  <button
                    className="tenant-wallet-top-up__primary"
                    disabled={Boolean(busy)}
                    onClick={close}
                    type="button"
                  >
                    Close and check later
                  </button>
                </>
              )}
            </div>
          </div>
        ) : null}

        {error ? (
          <p className="tenant-wallet-top-up__error" role="alert">{error}</p>
        ) : null}
      </section>
    </div>,
    document.body,
  );
}

export const _test = {
  amountUsdToMicros,
  amountZarToMinor,
  createIdempotencyKey,
  displayStatus,
  formatCharge,
  formatCountdown,
  formatCredit,
  formatFxRate,
  hostedPaymentUrl,
  normalizeAmountZar,
  secondsUntil,
  statusMessage,
  validConfiguration,
  validFxSnapshot,
  validPayment,
  validQuote,
};
